import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  Psychology as TherapistIcon,
  SupervisorAccount as SupervisorIcon
} from '@mui/icons-material';

function StatisticsTab({ serviceGroup, stats, members }) {
  // Calculate role-based counts from members
  const clientCount = members.filter(m => {
    const membership = m.serviceGroups?.find(sg => sg.groupId === serviceGroup._id);
    return membership?.role === 'client';
  }).length;

  const therapistCount = members.filter(m => {
    const membership = m.serviceGroups?.find(sg => sg.groupId === serviceGroup._id);
    return membership?.role === 'therapist';
  }).length;

  const supervisorCount = members.filter(m => {
    const membership = m.serviceGroups?.find(sg => sg.groupId === serviceGroup._id);
    return membership?.role === 'supervisor';
  }).length;

  const coordinatorCount = members.filter(m => {
    const membership = m.serviceGroups?.find(sg => sg.groupId === serviceGroup._id);
    return membership?.role === 'coordinator';
  }).length;

  const totalMembers = members.length;

  // Count active members
  const activeMembers = members.filter(m => m.status === 'Active').length;

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {Icon && <Icon sx={{ mr: 1, color: `${color}.main` }} />}
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h3" sx={{ mb: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Member Statistics for {serviceGroup.name}
      </Typography>

      {/* Overview Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Members"
            value={totalMembers}
            subtitle={`${activeMembers} active`}
            icon={PeopleIcon}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Clients"
            value={clientCount}
            subtitle={`${Math.round((clientCount / (totalMembers || 1)) * 100)}% of total`}
            icon={PersonIcon}
            color="info"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Therapists"
            value={therapistCount}
            subtitle={`${Math.round((therapistCount / (totalMembers || 1)) * 100)}% of total`}
            icon={TherapistIcon}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Clinical Supervisors"
            value={supervisorCount}
            subtitle={`${coordinatorCount} coordinator(s)`}
            icon={SupervisorIcon}
            color="warning"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Backend Stats (from ServiceGroup model) */}
      {stats && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Backend Statistics
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Last updated: {stats.lastUpdated ? new Date(stats.lastUpdated).toLocaleString() : 'Never'}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Clients (Backend)
                </Typography>
                <Typography variant="h5">
                  {stats.totalClients || 0}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Active Clients
                </Typography>
                <Typography variant="h5">
                  {stats.activeClients || 0}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Unassigned Clients
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {stats.unassignedClients || 0}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Therapists (Backend)
                </Typography>
                <Typography variant="h5">
                  {stats.totalTherapists || 0}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Supervisors (Backend)
                </Typography>
                <Typography variant="h5">
                  {stats.totalSupervisors || 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Group Information */}
      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Group Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Group Type
              </Typography>
              <Typography variant="body1">
                {serviceGroup.groupType || 'N/A'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1">
                {serviceGroup.status || 'N/A'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Assignment Mode
              </Typography>
              <Typography variant="body1">
                {serviceGroup.assignmentMode || 'N/A'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Max Clients Per Therapist
              </Typography>
              <Typography variant="body1">
                {serviceGroup.maxClientsPerTherapist || 'N/A'}
              </Typography>
            </Paper>
          </Grid>

          {serviceGroup.description && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">
                  {serviceGroup.description}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

export default StatisticsTab;
